import { cloneDeep, first, isEqual, join, last } from 'lodash';
import {
  calculateTime,
  cloneSubtitle,
  removePunctuation,
} from '../../common/functions';

// ***************************************************************************************
// onCloseContextMenu()
// permet de fermer le menu contextuel
// ***************************************************************************************
export function onCloseContextMenu() {
  if (this.state.contextMenu.display) {
    this.setState({
      contextMenu: {
        display: false,
        x: 0,
        y: 0,
        word: null,
      },
    });
  }
}

// ***************************************************************************************
// onShowContextMenu()
// permet d'ouvrir le menu contextuel
// ***************************************************************************************
export function onShowContextMenu(event) {
  if (this.props.readOnly) {
    return;
  }
  
  event.preventDefault();

  if (!this.currentWord || !this.currentWord.suggest) {
    this.onCloseContextMenu();
    return;
  }

  let x = this.currentWord.endX;
  let y = event.nativeEvent.layerY + 15;

  const width = 200;
  // 3 options
  const height = (this.currentWord.suggest.suggests.length + 3) * 32;

  const heightParent = event.currentTarget.parentElement.parentElement.parentElement.parentElement.clientHeight;

  if (x + width > this.width) {
    x = this.currentLetter.x + this.currentLetter.width;

    if (x + width > this.width) {
      x = this.currentLetter.x - width;
    }
  }

  if (y + height > heightParent) {
    y -= height;
  }

  if (y < 0) {
    y = 0;
  }

  this.setState({
    contextMenu: {
      display: true,
      x,
      y,
      word: this.currentWord,
    },
  });
}

// ***************************************************************************************
// onApplyCorrection()
// permet d'appliquer la correction choisie du menu contextuel sur les mots
// ***************************************************************************************
export function onApplyCorrection(word, suggest, remplacement) {
  if (suggest.errorType === 'spelling') {
    this.onApplyCorrectionForSpelling(word, suggest, remplacement);
  }

  if (suggest.errorType === 'grammar') {
    this.onApplyCorrectionForGrammar(
      this.currentLine,
      word,
      suggest,
      remplacement
    );
  }

  if (suggest.errorType === 'spelling-en') {
    this.onApplyCorrectionForSpellingEnglish(word, suggest, remplacement);
  }

  this.onCloseContextMenu();
  this.onChange();
}

export function onApplyCorrectionForSpelling(
  word,
  suggest,
  remplacement,
  applyUndo = true
) {
  if (applyUndo) {
    // UNDO
    this.addNewActionForUndo('onApplyCorrection');
  }

  word.text = word.text.replace(suggest.underlined, remplacement);

  word.suggest = undefined;
}

export function onApplyCorrectionForGrammar(
  currentLine,
  word,
  suggest,
  remplacement,
  applyUndo = true
) {
  const wordsToApply = currentLine.words.filter(
    (x, index) =>
      index >= word.index && index <= word.index + suggest.nbMatchingWords
  );

  if (wordsToApply.length === 1) {
    return this.onApplyCorrectionForSpelling(
      word,
      suggest,
      remplacement,
      applyUndo
    );
  }

  if (applyUndo) {
    // UNDO
    this.addNewActionForUndo('onApplyCorrection');
  }

  const firstWord = first(wordsToApply);
  const lastWord = last(wordsToApply);

  // La phrase complète des mots sur lesquels on va appliquer la correction
  const sentenceToApply = join(
    wordsToApply.map((x) => x.text),
    ' '
  );

  const wordsSuggested = remplacement.split(' ');

  // La phrase complète de la correction suggérée
  const sentenceSuggested = join(wordsSuggested, ' ');

  const newSentence = sentenceToApply.replace(
    suggest.underlined,
    sentenceSuggested
  );

  if (wordsToApply.length === 1) {
    word.text = newSentence;
    return;
  }

  const newWords = [];

  // on créé un nouveau tableau de mot et on recalcule le timecode
  newSentence.split(' ').forEach((text, index) => {
    if (index === 0) {
      const newWord = {
        ...firstWord,
        text: text,
        in: firstWord.in,
        out: lastWord.out,
        letters: [],
        suggest: undefined,
      };

      newWords.push(newWord);
    } else {
      const inTime = newWords[index - 1].in;

      let inOutTime = calculateTime(
        inTime,
        lastWord.out,
        sentenceToApply,
        text
      );

      const newWord = {
        ...firstWord,
        index: firstWord.index + index,
        newline: false,
        text: text,
        in: inOutTime,
        out: lastWord.out,
        letters: [],
        suggest: undefined,
      };

      newWords[index - 1].out = inOutTime;

      newWords.push(newWord);
    }
  });

  // Supprimer de la liste et ajouter ces nouveaux mots dans la liste
  currentLine.words.splice(firstWord.index, wordsToApply.length, ...newWords);
}

export function onApplyCorrectionForSpellingEnglish(
  word,
  suggest,
  remplacement,
  applyUndo = true
) {
  if (applyUndo) {
    // UNDO
    this.addNewActionForUndo('onApplyCorrection');
  }

  word.text = word.text.replace(suggest.underlined, remplacement);

  word.suggest = undefined;
}

export function onApplyCorrectionAll(words) {
  this.onCloseContextMenu();

  const oldSubtitles = cloneDeep(this.props.subtitles);
  const currentSubtitleId = this.subtitle.id;

  const subtitlesIds = words.map((x) => x.suggest.subtitleId);

  const subtitlesSelected = this.props.subtitles.filter((x) =>
    subtitlesIds.includes(x.id)
  );

  words.forEach((word) => {
    const suggest = word.suggest;
    const remplacement = suggest.suggests[0];

    const subtitle = subtitlesSelected.find((x) => x.id === suggest.subtitleId);

    let currentLine;
    let wordToUpdate;
    if (word.indexLine !== undefined) {
      currentLine = subtitle.lines[word.indexLine];
      wordToUpdate = currentLine.words[word.index];
    } else {
      currentLine = subtitle.lines.find((x) => x.words.includes(word));
      const index = currentLine.words.findIndex((x) => isEqual(x, word));
      wordToUpdate = currentLine.words[index];
      wordToUpdate.index = index;
    }

    if (suggest.errorType === 'spelling') {
      this.onApplyCorrectionForSpelling(
        wordToUpdate,
        suggest,
        remplacement,
        false
      );
    }

    if (suggest.errorType === 'grammar') {
      this.onApplyCorrectionForGrammar(
        currentLine,
        wordToUpdate,
        suggest,
        remplacement,
        false
      );
    }

    if (suggest.errorType === 'spelling-en') {
       this.onApplyCorrectionForSpellingEnglish(wordToUpdate, suggest, remplacement, false);
    }
  });

  // on historise
  this.addSubtitlesToUndoOrRedo("onApplyCorrectionAll", oldSubtitles, currentSubtitleId, subtitlesSelected, currentSubtitleId);

  // on envoie subtitlesSelected à mettre à jour
  this.props.onUpdateSubtitles(
    subtitlesSelected.map((x) => cloneSubtitle(x)),
    currentSubtitleId
  );
}

export function onAddWordToDictionary(suggests) {
  this.onCloseContextMenu();
  alert("Cette fonctionnalité n'a pas été encore implémentée.");
}

export function onIgnoreWordForCorrection(word) {
  const text = removePunctuation(word.text);
  this.props.grammalecte.ignoreWord(text);

  this.currentWord.suggest = null;
  this.onCloseContextMenu();
  this.onChange();
}

export function onIgnoreSuggestForCorrection(word) {
  this.props.grammalecte.ignoreSuggestWord(word);

  this.currentWord.suggest = null;

  this.onCloseContextMenu();
  this.onChange();
}
